import Swiper, { Navigation } from 'swiper';

const initializedSections = new WeakSet();
const loadedSlides = new WeakSet();
const slideLoadPromises = new WeakMap();
const prewarmedSlides = new WeakSet();
const slidePrewarmPromises = new WeakMap();
const reducedMotionMediaQuery = '(prefers-reduced-motion: reduce)';
const portfolioFlipbookImageLoadTimeout = 20000;
const portfolioFlipbookRetryDelay = 1000;
export const portfolioFlipbookPortraitMediaQuery =
  '(max-width: 1024px) and (orientation: portrait)';
export const portfolioFlipbookMobileSliderMediaQuery = '(max-width: 640px)';

export const shouldUsePortfolioMobileSlider = (viewportWidth) =>
  Number(viewportWidth) <= 640;

export const getPortfolioFlipbookScrollScreens = (slideCount) => {
  if (slideCount <= 27) {
    return 6;
  }

  return 6 + Math.ceil((slideCount - 27) / 10);
};

export const getPortfolioFlipbookPreloadOrder = (
  slideCount,
  isReverse = false
) =>
  Array.from(
    { length: slideCount },
    (_, index) => (isReverse ? slideCount - index - 1 : index)
  );

export const getPortfolioFlipbookPrewarmIndexes = (slideCount) => {
  if (slideCount <= 0) {
    return [];
  }

  if (slideCount === 1) {
    return [0];
  }

  return [0, slideCount - 1];
};

export const getPortfolioFlipbookPortraitPairs = (slideCount) =>
  slideCount < 2
    ? []
    : Array.from({ length: slideCount - 1 }, (_, index) => [index, index + 1]);

const getPortfolioFlipbookSections = (scope) => {
  if (!scope?.querySelectorAll) {
    return [];
  }

  return scope.matches?.('[data-portfolio-flipbook]')
    ? [scope]
    : Array.from(scope.querySelectorAll('[data-portfolio-flipbook]'));
};

const preloadImageSource = (source) =>
  new Promise((resolve) => {
    if (!source || typeof Image !== 'function') {
      resolve('error');
      return;
    }

    let image;
    let timeoutId;
    let isResolved = false;
    const cleanup = () => {
      image?.removeEventListener('load', handleLoad);
      image?.removeEventListener('error', handleError);
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
    const finish = (result) => {
      if (isResolved) {
        return;
      }

      isResolved = true;
      cleanup();
      resolve(result);
    };
    const handleLoad = () => {
      if (typeof image.decode === 'function') {
        try {
          image.decode().catch(() => undefined);
        } catch {}
      }

      finish('loaded');
    };
    const handleError = () => finish('error');

    try {
      image = new Image();
      image.addEventListener('load', handleLoad, { once: true });
      image.addEventListener('error', handleError, { once: true });
      image.decoding = 'async';
      timeoutId = setTimeout(
        () => finish('timeout'),
        portfolioFlipbookImageLoadTimeout
      );
      image.src = source;
    } catch {
      finish('error');
    }
  });

export const loadPortfolioFlipbookSlide = (slide) => {
  if (!slide) {
    return Promise.resolve(false);
  }

  if (loadedSlides.has(slide)) {
    return Promise.resolve(true);
  }

  const existingPromise = slideLoadPromises.get(slide);

  if (existingPromise) {
    return existingPromise;
  }

  const source = slide.querySelector('source[data-srcset]');
  const image = slide.querySelector('img[data-src]');
  const preferredUrl = source?.dataset.srcset || '';
  const fallbackUrl = image?.dataset.src || '';

  if (!image || (!preferredUrl && !fallbackUrl)) {
    return Promise.resolve(true);
  }

  const loadPromise = new Promise((resolve) => {
    let isFallback = !preferredUrl;
    let isResolved = false;

    const cleanup = () => {
      image.removeEventListener('load', handleLoad);
      image.removeEventListener('error', handleError);
    };
    const finish = (result) => {
      if (isResolved) {
        return;
      }

      isResolved = true;
      cleanup();
      resolve(result);
    };
    const handleLoad = () => {
      if (source?.dataset.srcset) {
        delete source.dataset.srcset;
      }

      if (image.dataset.src) {
        delete image.dataset.src;
      }

      loadedSlides.add(slide);
      finish(true);
    };
    const handleError = () => {
      if (!isFallback && fallbackUrl) {
        isFallback = true;
        source?.removeAttribute('srcset');
        image.src = fallbackUrl;
        settleCompletedImage();
        return;
      }

      finish(false);
    };
    const settleCompletedImage = () => {
      if (!image.complete) {
        return;
      }

      queueMicrotask(() => {
        if (!isResolved && image.complete) {
          if (image.naturalWidth > 0) {
            handleLoad();
          } else {
            handleError();
          }
        }
      });
    };

    image.addEventListener('load', handleLoad);
    image.addEventListener('error', handleError);
    image.loading = 'eager';

    try {
      if (preferredUrl && source) {
        source.srcset = preferredUrl;
      } else {
        source?.removeAttribute('srcset');
      }

      image.src = fallbackUrl || preferredUrl;
      settleCompletedImage();
    } catch {
      finish(false);
    }
  });

  slideLoadPromises.set(slide, loadPromise);
  const clearLoadPromise = () => {
    if (slideLoadPromises.get(slide) === loadPromise) {
      slideLoadPromises.delete(slide);
    }
  };

  loadPromise.then(clearLoadPromise, clearLoadPromise);

  return loadPromise;
};

const prewarmPortfolioFlipbookSlide = (slide) => {
  if (!slide) {
    return Promise.resolve(false);
  }

  if (prewarmedSlides.has(slide)) {
    return Promise.resolve(true);
  }

  const existingPromise = slidePrewarmPromises.get(slide);

  if (existingPromise) {
    return existingPromise;
  }

  const source = slide.querySelector('source[data-srcset]');
  const image = slide.querySelector('img[data-src]');
  const preferredUrl = source?.dataset.srcset || '';
  const fallbackUrl = image?.dataset.src || '';

  if (!preferredUrl && !fallbackUrl) {
    return Promise.resolve(true);
  }

  const prewarmPromise = (async () => {
    const preferredResult = preferredUrl
      ? await preloadImageSource(preferredUrl)
      : 'error';

    if (preferredResult === 'loaded') {
      prewarmedSlides.add(slide);
      return true;
    }

    if (preferredResult === 'timeout') {
      return false;
    }

    if (
      fallbackUrl &&
      (await preloadImageSource(fallbackUrl)) === 'loaded'
    ) {
      prewarmedSlides.add(slide);
      return true;
    }

    return false;
  })();

  slidePrewarmPromises.set(slide, prewarmPromise);
  const clearPrewarmPromise = () => {
    if (slidePrewarmPromises.get(slide) === prewarmPromise) {
      slidePrewarmPromises.delete(slide);
    }
  };

  prewarmPromise.then(clearPrewarmPromise, clearPrewarmPromise);

  return prewarmPromise;
};

export const prewarmPortfolioFlipbookEdges = (scope = document) => {
  const loadPromises = getPortfolioFlipbookSections(scope).flatMap((section) => {
    const slides = Array.from(
      section.querySelectorAll('[data-portfolio-flipbook-slide]')
    );

    return getPortfolioFlipbookPrewarmIndexes(slides.length).map((index) =>
      prewarmPortfolioFlipbookSlide(slides[index])
    );
  });

  if (!loadPromises.length) {
    return Promise.resolve(false);
  }

  return Promise.all(loadPromises).then((results) => results.every(Boolean));
};

const waitForPortfolioFlipbookRetry = () =>
  new Promise((resolve) => setTimeout(resolve, portfolioFlipbookRetryDelay));

const preloadPortfolioFlipbookSlides = async (slides) => {
  const results = await Promise.all(slides.map(loadPortfolioFlipbookSlide));
  const failedSlides = slides.filter((slide, index) => !results[index]);

  if (!failedSlides.length) {
    return true;
  }

  await waitForPortfolioFlipbookRetry();
  const retryResults = await Promise.all(
    failedSlides.map(loadPortfolioFlipbookSlide)
  );

  return retryResults.every(Boolean);
};

export const initPortfolioFlipbook = (scope = document) => {
  const sections = getPortfolioFlipbookSections(scope);

  sections.forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    initializedSections.add(section);

    const pinEl = section.querySelector('.portfolio-sec__pin');
    const slides = Array.from(
      section.querySelectorAll('[data-portfolio-flipbook-slide]')
    );
    const slideCount = slides.length;
    const sliderWrapper = section.querySelector('.portfolio-swiper-wr');
    const sliderEl = sliderWrapper?.querySelector('.swiper');
    const nextEl = sliderWrapper?.querySelector('.swiper-button-next');
    const prevEl = sliderWrapper?.querySelector('.swiper-button-prev');

    prewarmPortfolioFlipbookEdges(section);
    preloadPortfolioFlipbookSlides(slides);

    const gsapInstance = globalThis.gsap;
    const scrollTrigger = globalThis.ScrollTrigger;
    const mobileSliderMedia =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(portfolioFlipbookMobileSliderMediaQuery)
        : null;
    const isMobileSliderActive = () =>
      mobileSliderMedia?.matches ??
      shouldUsePortfolioMobileSlider(window.innerWidth);
    const canAnimate =
      pinEl &&
      slideCount > 1 &&
      gsapInstance &&
      scrollTrigger &&
      !window.matchMedia(reducedMotionMediaQuery).matches;
    const canCreateMobileSlider = sliderEl && slideCount > 1;

    if (!canAnimate && !canCreateMobileSlider) {
      return;
    }

    if (canAnimate) {
      gsapInstance.registerPlugin(scrollTrigger);
    }

    const portraitMedia =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(portfolioFlipbookPortraitMediaQuery)
        : null;
    const slideMedia = slides
      .map((slide) => slide.querySelector('.portfolio-sec__slide-media'))
      .filter(Boolean);
    let entranceTween;
    let timeline;
    let mobileSlider;

    const clearAnimation = () => {
      entranceTween?.scrollTrigger?.kill();
      entranceTween?.kill();
      timeline?.scrollTrigger?.kill();
      timeline?.kill();
      entranceTween = undefined;
      timeline = undefined;
      gsapInstance?.set(slides, {
        clearProps: 'transform,visibility,opacity,zIndex,--portfolio-slide-overlay-opacity',
      });
      gsapInstance?.set(slideMedia, { clearProps: 'transform' });
      section.classList.remove('is-enhanced', 'is-portrait-pairs');
    };

    const destroyMobileSlider = () => {
      mobileSlider?.destroy(true, true);
      mobileSlider = undefined;
      section.classList.remove('is-mobile-slider');
    };

    const initMobileSlider = () => {
      if (!canCreateMobileSlider || mobileSlider || sliderEl.swiper) {
        return;
      }

      section.classList.add('is-mobile-slider');
      mobileSlider = new Swiper(sliderEl, {
        modules: [Navigation],
        slidesPerView: 1,
        speed: 450,
        threshold: 10,
        watchOverflow: true,
        navigation:
          nextEl && prevEl
            ? {
                nextEl,
                prevEl,
              }
            : undefined,

      });
    };

    const addPreviousSlideDepth = (animationTimeline, previousSlide, position) => {
      const previousPicture = previousSlide.querySelector('.portfolio-sec__slide-media');

      animationTimeline.to(
        previousSlide,
        {
          '--portfolio-slide-overlay-opacity': 0.22,
          duration: 0.5,
          ease: 'none',
        },
        position + 0.5
      );

      if (previousPicture) {
        animationTimeline.to(
          previousPicture,
          {
            scale: 1.02,
            duration: 0.5,
            ease: 'none',
          },
          position + 0.5
        );
      }
    };

    const createTimeline = (isPortraitPairs) => {
      const portraitPairs = getPortfolioFlipbookPortraitPairs(slideCount);
      const entranceSlides = isPortraitPairs ? slides.slice(0, 2) : [slides[0]];
      const initialSlidePosition = isPortraitPairs ? 200 : 100;
      const entranceOffset = 0;

      section.classList.add('is-enhanced');
      section.classList.toggle('is-portrait-pairs', isPortraitPairs);
      gsapInstance.set(slides, {
        autoAlpha: 1,
        yPercent: initialSlidePosition,
        zIndex: (index) => index + 1,
      });
      gsapInstance.set(entranceSlides, {
        yPercent: (index) => index * 100 + entranceOffset,
      });
      entranceTween = gsapInstance.to(entranceSlides, {
        yPercent: (index) => index * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'top top',
          scrub: true,
          invalidateOnRefresh: true,
        },
      });

      if (isPortraitPairs && portraitPairs.length === 1) {
        return;
      }

      timeline = gsapInstance.timeline({
        scrollTrigger: {
          trigger: section,
          pin: pinEl,
          start: 'top top',
          end: () => `+=${window.innerHeight * getPortfolioFlipbookScrollScreens(slideCount)}`,
          scrub: 0.2,
          invalidateOnRefresh: true,
        },
      });

      if (isPortraitPairs) {
        portraitPairs.slice(1).forEach(([topIndex, bottomIndex], index) => {
          const position = index;
          const previousSlide = slides[topIndex - 1];

          timeline.to(
            slides[topIndex],
            {
              yPercent: 0,
              duration: 1,
              ease: 'none',
            },
            position
          );
          timeline.to(
            slides[bottomIndex],
            {
              yPercent: 100,
              duration: 1,
              ease: 'none',
            },
            position
          );
          addPreviousSlideDepth(timeline, previousSlide, position);
        });

        return;
      }

      slides.forEach((slide, index) => {
        if (index === 0) {
          return;
        }

        const position = index - 1;
        const previousSlide = slides[index - 1];

        timeline.to(
          slide,
          {
            yPercent: 0,
            duration: 1,
            ease: 'none',
          },
          position
        );
        addPreviousSlideDepth(timeline, previousSlide, position);
      });
    };

    const rebuildAnimation = () => {
      if (isMobileSliderActive()) {
        clearAnimation();
        initMobileSlider();
        scrollTrigger?.refresh();
        return;
      }

      destroyMobileSlider();

      if (!canAnimate) {
        return;
      }

      clearAnimation();
      createTimeline(Boolean(portraitMedia?.matches));
      scrollTrigger.refresh();
    };

    rebuildAnimation();
    if (portraitMedia?.addEventListener) {
      portraitMedia.addEventListener('change', rebuildAnimation);
    } else if (portraitMedia?.addListener) {
      portraitMedia.addListener(rebuildAnimation);
    }
    if (mobileSliderMedia?.addEventListener) {
      mobileSliderMedia.addEventListener('change', rebuildAnimation);
    } else if (mobileSliderMedia?.addListener) {
      mobileSliderMedia.addListener(rebuildAnimation);
    }
  });
};

globalThis.initPortfolioFlipbook = initPortfolioFlipbook;
globalThis.portfolioFlipbookPrewarm = prewarmPortfolioFlipbookEdges;

if (typeof document !== 'undefined' && !document.querySelector('.bold-loader-new')) {
  initPortfolioFlipbook();
}
